/** Per-match kit payload helpers (EPIC-112). */
import type { MatchTeamKit } from "../types";

/**
 * True when a kit carries a shirt colour worth sending (EPIC-113 D2: shirt-only
 * payload). A label-only kit (e.g. "Sin color" on a labelled kit) is empty.
 */
export const kitHasContent = (kit?: MatchTeamKit | null): kit is MatchTeamKit =>
  !!kit && !!kit.shirt_color;

const sig = (kit?: MatchTeamKit | null): string =>
  JSON.stringify(
    kitHasContent(kit)
      ? [kit.shirt_color ?? null, kit.labels?.shirt ?? null]
      : null,
  );

/**
 * Edit-mode kit for PUT /matches/{id}: `undefined` = unchanged (omit, the
 * server leaves it), `null` = the user emptied a stored kit (clear), else the
 * new kit.
 */
export function resolveKitForEdit(
  current: MatchTeamKit | null | undefined,
  original: MatchTeamKit | null | undefined,
): MatchTeamKit | null | undefined {
  if (sig(current) === sig(original)) return undefined;
  // EPIC-113 D2: send only the shirt, never a stored legacy shorts/socks.
  return kitHasContent(current)
    ? {
        shirt_color: current.shirt_color ?? null,
        labels: { shirt: current.labels?.shirt ?? null },
      }
    : null;
}

/** Builds the `home_team`/`away_team` fragments of a PUT body (omitted when unchanged). */
export function buildKitUpdate(
  homeKit: MatchTeamKit | null,
  awayKit: MatchTeamKit | null,
  originalHome: MatchTeamKit | null | undefined,
  originalAway: MatchTeamKit | null | undefined,
): {
  home_team?: { kit: MatchTeamKit | null };
  away_team?: { kit: MatchTeamKit | null };
} {
  const home = resolveKitForEdit(homeKit, originalHome);
  const away = resolveKitForEdit(awayKit, originalAway);
  return {
    ...(home !== undefined ? { home_team: { kit: home } } : {}),
    ...(away !== undefined ? { away_team: { kit: away } } : {}),
  };
}
